import { getDaysInMonth } from 'date-fns';
import { DateFields } from './DatePicker';

export type DateParsingErrors = 'day' | 'month' | 'year';
export type BoundedDateParsingErrors = DateParsingErrors | 'min' | 'max';
type BaseParsedDate<E> =
  | {
      isValid: true;
      date: Date;
    }
  | {
      isValid: false;
      // Uniqueness of errors is not enforced by data structure, and is not
      // necessary for current implementation. Enforcing this through a Set is
      // considerably more verbose.
      errors: Array<E>;
    };

export type ParsedDate = BaseParsedDate<DateParsingErrors>;
export type BoundedParsedDate = BaseParsedDate<BoundedDateParsingErrors>;

export const parseBoundedDateField = (
  date: DateFields,
  // Nested to ensure, that they can be optional and specified independently.
  bounds?: {
    minIncluded?: Date;
    maxIncluded?: Date;
  }
): BoundedParsedDate => {
  const parsed = parseDateField(date);

  if (parsed.isValid === false) {
    // Only possible to check min, max when date is valid.
    return parsed;
  } else if (
    bounds?.minIncluded !== undefined &&
    bounds.minIncluded > parsed.date
  ) {
    return {
      isValid: false,
      errors: ['min' as const],
    };
  } else if (
    bounds?.maxIncluded !== undefined &&
    parsed.date > bounds.maxIncluded
  ) {
    return {
      isValid: false,
      errors: ['max' as const],
    };
  } else {
    return parsed;
  }
};

export const parseDateField = (date: DateFields): ParsedDate => {
  // First need to check year and date, because this is needed to validate day.
  const year = parseYear(date.YYYY);
  const month = parseMonth(date.MM);
  const day =
    year !== null && month !== null ? parseDay(date.DD, month, year) : null;

  const errors = [
    ...(year === null ? ['year' as const] : []),
    ...(month === null ? ['month' as const] : []),
    ...(day === null ? ['day' as const] : []),
  ];

  return errors.length > 0
    ? { isValid: false, errors }
    : { isValid: true, date: new Date(year!, month! - 1, day!) };
};

export const parseDay = (
  DD: string,
  month: number,
  year: number
): number | null => {
  if (DD.length > 2) {
    return null;
  }
  const dayNumber = parseIntStrict(DD);
  const daysInMonth = getDaysInMonth(new Date(year, month - 1));
  return dayNumber !== null && dayNumber <= daysInMonth && dayNumber > 0
    ? dayNumber
    : null;
};

/** Returns month number, not the index */
export const parseMonth = (MM: string) => {
  if (MM.length > 2) {
    return null;
  }

  const monthNumber = parseIntStrict(MM);
  return monthNumber !== null && monthNumber <= 12 && monthNumber > 0
    ? monthNumber
    : null;
};

/**
 * Only supports years 1000 to 9999, we restrict years below 1000, because
 * when a new date is created with the constructor, one and two digits are
 * interpreted as 19xx.
 */
export const parseYear = (YYYY: string) =>
  YYYY.length === 4 && YYYY >= '1000' ? parseIntStrict(YYYY) : null;

/**
 * Wrapper around `parseInt`, disallows emtpy strings and non-numeric characters
 * in the string as a suffix.
 *
 * Negative numbers are not supported.
 */
export const parseIntStrict = (str: string): number | null =>
  isDigitString(str) ? parseInt(str) : null;

/** Negative numbers not supported */
export const isDigitString = (str: string) => /^[0-9]+$/.test(str);
